import type { MetricSeries } from '@bartizo/shared';
import { useMemo, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { formatInstant } from '../lib/time.js';

/**
 * A time series, drawn (knowledge doc sections 8.3 and 15).
 *
 * Inline SVG rather than a charting library, for the reason P2.1 chose hand-written
 * CSS: the console draws a handful of small line charts, and a chart library is a
 * build plugin, a theming layer and a bundle for something a `<path>` already does.
 *
 * Three rules from the visualization guidance are structural rather than cosmetic:
 *
 * - **Identity is never colour alone.** Every series is listed underneath with its
 *   name and its current value in text, which is also the table view that three of
 *   the light-mode series colours require to clear contrast.
 * - **Hues are assigned in fixed order and never cycled.** A ninth series would get
 *   a repeated colour, so the chart shows eight and says how many it left out —
 *   the API has already ranked them, so the eight shown are the ones that matter.
 * - **The hover layer is part of the chart.** A crosshair snaps to a bucket and one
 *   readout lists every series there, so nobody has to aim at a 2px line. Arrow
 *   keys do the same thing, because a chart that is only hoverable is a chart half
 *   the fleet cannot read.
 */

/** Matched by `--chart-1` … `--chart-8` in `index.css`, in this order. */
export const CHART_SERIES_MAX = 8;

const PADDING = { top: 12, right: 16, bottom: 26, left: 52 };
const DEFAULT_HEIGHT = 200;
/**
 * The drawing grid. The SVG keeps its aspect ratio and fills the card's width, so
 * these are proportions rather than pixels — and the text inside is not stretched,
 * which `preserveAspectRatio="none"` would have done to it.
 */
const VIEW_WIDTH = 720;

export interface MetricChartProps {
  series: MetricSeries[];
  /** The range the API actually used, so the axis matches the request. */
  from: string;
  to: string;
  /**
   * The bucket the API actually used. A step between readings wider than this is a
   * gap in the data rather than a slow stretch, and is drawn as one.
   */
  bucketSeconds: number;
  /** Turns a value into what the axis and the readout show. */
  format: (value: number) => string;
  /** Names each line: the label that distinguishes one from another. */
  seriesLabel: (series: MetricSeries) => string;
  /** Pinned for a percentage, so 40% and 41% do not look like a cliff. */
  yMin?: number;
  yMax?: number;
  timezone: string;
  /** What the chart is of, for a screen reader and for the crosshair readout. */
  title: string;
  height?: number;
  /** What an empty range says; the default is about disks, where the chart began. */
  emptyText?: string;
}

interface Column {
  at: string;
  /** Milliseconds since the epoch, so a gap is a subtraction rather than a re-parse. */
  atMs: number;
  x: number;
  values: (number | null)[];
}

export function MetricChart({
  series,
  from,
  to,
  bucketSeconds,
  format,
  seriesLabel,
  yMin,
  yMax,
  timezone,
  title,
  height = DEFAULT_HEIGHT,
  emptyText = 'Nothing has been recorded in this range yet. A server reports its disks every couple of minutes.',
}: MetricChartProps): ReactNode {
  const [hover, setHover] = useState<number | null>(null);

  const shown = useMemo(() => series.slice(0, CHART_SERIES_MAX), [series]);
  const hidden = series.length - shown.length;
  /** Resolved once, so nothing downstream has to index back into `shown`. */
  const names = shown.map(seriesLabel);
  const hue = (index: number): string => `var(--chart-${String((index % CHART_SERIES_MAX) + 1)})`;

  const layout = useMemo(
    () => buildLayout({ series: shown, from, to, yMin, yMax, height }),
    [shown, from, to, yMin, yMax, height],
  );

  if (layout === null) {
    return <p className="muted text-sm">{emptyText}</p>;
  }

  const { columns, scaleY, ticks } = layout;
  const active = hover === null ? null : columns[hover];

  const moveTo = (index: number): void => {
    setHover(Math.max(0, Math.min(columns.length - 1, index)));
  };

  const onPointerMove = (event: PointerEvent<SVGSVGElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width === 0) return;
    // The SVG scales to its box, so the pointer is mapped back into view units
    // before it is compared with a column's x.
    const x = ((event.clientX - box.left) / box.width) * VIEW_WIDTH;
    let nearest = 0;
    for (const [index, column] of columns.entries()) {
      const best = columns[nearest];
      if (best === undefined) break;
      if (Math.abs(column.x - x) < Math.abs(best.x - x)) nearest = index;
    }
    setHover(nearest);
  };

  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>): void => {
    if (event.key === 'ArrowRight') moveTo((hover ?? -1) + 1);
    else if (event.key === 'ArrowLeft') moveTo((hover ?? columns.length) - 1);
    else if (event.key === 'Home') moveTo(0);
    else if (event.key === 'End') moveTo(columns.length - 1);
    else if (event.key === 'Escape') setHover(null);
    else return;
    event.preventDefault();
  };

  return (
    <div className="chart">
      <div className="chart-plot">
        <svg
          viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(height)}`}
          className="chart-svg"
          role="img"
          aria-label={`${title}. ${String(shown.length)} series over ${formatInstant(from, { timezone })} to ${formatInstant(to, { timezone })}. The values are listed in the table below.`}
          tabIndex={0}
          onPointerMove={onPointerMove}
          onPointerLeave={() => {
            setHover(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setHover(null);
          }}
        >
          {ticks.map((tick) => (
            <g key={tick.value}>
              {/* Hairline, solid, recessive: the data is the only loud thing. */}
              <line
                x1={PADDING.left}
                x2={VIEW_WIDTH - PADDING.right}
                y1={scaleY(tick.value)}
                y2={scaleY(tick.value)}
                className="chart-grid"
              />
              <text
                x={PADDING.left - 8}
                y={scaleY(tick.value) + 4}
                textAnchor="end"
                className="chart-axis-text"
              >
                {tick.label}
              </text>
            </g>
          ))}

          {shown.map((line, index) => (
            <path
              key={names[index]}
              d={pathFor(columns, index, scaleY, bucketSeconds)}
              fill="none"
              stroke={hue(index)}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {active !== undefined && active !== null && (
            <>
              <line
                x1={active.x}
                x2={active.x}
                y1={PADDING.top}
                y2={height - PADDING.bottom}
                className="chart-crosshair"
              />
              {active.values.map((value, index) =>
                value === null ? null : (
                  <circle
                    key={names[index]}
                    cx={active.x}
                    cy={scaleY(value)}
                    r={4}
                    fill={hue(index)}
                    // A 2px surface ring, so a marker stays legible where two lines
                    // cross each other.
                    stroke="var(--surface)"
                    strokeWidth={2}
                  />
                ),
              )}
            </>
          )}

          <text x={PADDING.left} y={height - 8} textAnchor="start" className="chart-axis-text">
            {formatInstant(from, { timezone })}
          </text>
          <text
            x={VIEW_WIDTH - PADDING.right}
            y={height - 8}
            textAnchor="end"
            className="chart-axis-text"
          >
            {formatInstant(to, { timezone })}
          </text>
        </svg>

        {active !== undefined && active !== null && (
          <div
            className={`chart-readout${active.x > VIEW_WIDTH / 2 ? ' flip' : ''}`}
            style={{ left: `${String((active.x / VIEW_WIDTH) * 100)}%` }}
            role="status"
          >
            <p className="chart-readout-time">{formatInstant(active.at, { timezone })}</p>
            <ul>
              {active.values.map((value, index) => (
                <li key={names[index]}>
                  <span
                    className="chart-key"
                    style={{ background: hue(index) }}
                    aria-hidden="true"
                  />
                  {/* Values lead, names follow: the reader has the series already. */}
                  <strong>{value === null ? '—' : format(value)}</strong>
                  <span className="muted">{names[index]}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* The table view. It is also what carries identity when colour cannot. */}
      <table className="chart-legend">
        <caption className="visually-hidden">{title}, latest value per series</caption>
        <thead>
          <tr>
            <th scope="col">Series</th>
            <th scope="col">Latest</th>
            <th scope="col">Range</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((line, index) => {
            const last = line.points[line.points.length - 1];
            const low = Math.min(...line.points.map((point) => point.min));
            const high = Math.max(...line.points.map((point) => point.max));
            return (
              <tr key={names[index]}>
                <th scope="row">
                  <span
                    className="chart-key"
                    style={{ background: hue(index) }}
                    aria-hidden="true"
                  />
                  {names[index]}
                </th>
                <td>{last === undefined ? '—' : format(last.avg)}</td>
                <td className="muted">
                  {line.points.length === 0 ? '—' : `${format(low)} – ${format(high)}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {hidden > 0 && (
        <p className="muted text-xs">
          Showing the {String(CHART_SERIES_MAX)} largest of {String(series.length)}.
        </p>
      )}
    </div>
  );
}

interface Layout {
  columns: Column[];
  scaleY: (value: number) => number;
  ticks: { value: number; label: string }[];
}

interface LayoutInput {
  series: MetricSeries[];
  from: string;
  to: string;
  yMin: number | undefined;
  yMax: number | undefined;
  height: number;
}

/**
 * Turns the API's per-series points into one shared time axis.
 *
 * A series can miss a bucket — a `du` scan that timed out, an agent that was
 * restarting — so the columns are the union of every series' bucket times and a
 * missing reading is `null` rather than a point interpolated out of nothing.
 */
function buildLayout({ series, from, to, yMin, yMax, height }: LayoutInput): Layout | null {
  const times = new Set<string>();
  for (const line of series) for (const point of line.points) times.add(point.at);
  const sorted = [...times].sort();
  if (sorted.length === 0) return null;

  const start = Date.parse(from);
  const end = Date.parse(to);
  const span = end - start > 0 ? end - start : 1;
  const plotWidth = VIEW_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = height - PADDING.top - PADDING.bottom;

  const byTime = series.map((line) => new Map(line.points.map((point) => [point.at, point.avg])));

  const columns: Column[] = sorted.map((at) => ({
    at,
    atMs: Date.parse(at),
    x: PADDING.left + ((Date.parse(at) - start) / span) * plotWidth,
    values: byTime.map((points) => points.get(at) ?? null),
  }));

  const observed = series.flatMap((line) => line.points.flatMap((point) => [point.min, point.max]));
  let low = yMin ?? Math.min(...observed);
  let high = yMax ?? Math.max(...observed);
  if (!Number.isFinite(low) || !Number.isFinite(high)) return null;
  if (low === high) {
    // A perfectly flat series still needs a band to be drawn in, or every point
    // lands on the same pixel and the line disappears into the axis.
    low -= 1;
    high += 1;
  }

  const scaleY = (value: number): number =>
    PADDING.top + plotHeight - ((value - low) / (high - low)) * plotHeight;

  return { columns, scaleY, ticks: ticksFor(low, high) };
}

/**
 * Three gridlines: enough to read a value off, few enough to stay recessive.
 *
 * Every tick on an axis is formatted the same way, decided once from the band
 * rather than per value. Deciding per value put `100`, `50` and `0.0` on one axis
 * the first time this was looked at in a browser, which reads as three different
 * quantities rather than as one scale.
 */
function ticksFor(low: number, high: number): { value: number; label: string }[] {
  const magnitude = Math.max(Math.abs(low), Math.abs(high));
  const unit =
    magnitude >= 1_000_000_000
      ? { divisor: 1_000_000_000, suffix: 'G' }
      : magnitude >= 1_000_000
        ? { divisor: 1_000_000, suffix: 'M' }
        : magnitude >= 1_000
          ? { divisor: 1_000, suffix: 'k' }
          : { divisor: 1, suffix: '' };

  // Decimals come from how wide the band is, not from how big the numbers are: a
  // chart spanning 88.1 to 88.4 needs them, one spanning 0 to 100 does not.
  const span = Math.abs(high - low) / unit.divisor;
  const decimals = span >= 10 ? 0 : span >= 1 ? 1 : 2;

  return [low, low + (high - low) / 2, high].map((value) => ({
    value,
    label: `${(value / unit.divisor).toFixed(decimals)}${unit.suffix}`,
  }));
}

/**
 * How far apart two readings may be before the line between them is a guess.
 *
 * One and a half buckets, so a bucket that merely landed a little late still joins
 * up and a missed one does not.
 */
const GAP_FACTOR = 1.5;

/**
 * The `d` for one line, broken wherever the series has no reading.
 *
 * A gap is drawn as a gap. Joining across one would invent a straight line through
 * however long the agent was down, which on a disk chart reads as a period of calm
 * growth rather than as no data at all.
 *
 * Two things make a gap: a column where this series has no value — which happens
 * when another series was reporting and this one was not — and a step between
 * consecutive columns wider than a bucket and a half, which is the only way a lone
 * series can show one, since a bucket nobody reported is not a column.
 */
function pathFor(
  columns: Column[],
  index: number,
  scaleY: (value: number) => number,
  bucketSeconds: number,
): string {
  const maxStepMs = bucketSeconds * 1000 * GAP_FACTOR;
  let path = '';
  let previousMs: number | null = null;

  for (const column of columns) {
    const value = column.values[index];
    if (value === null || value === undefined) {
      previousMs = null;
      continue;
    }
    const joins = previousMs !== null && column.atMs - previousMs <= maxStepMs;
    const point = `${column.x.toFixed(2)} ${scaleY(value).toFixed(2)}`;
    path += joins ? ` L ${point}` : `${path === '' ? '' : ' '}M ${point}`;
    previousMs = column.atMs;
  }

  return path;
}
